import React, { useState } from 'react';
import ThreediumSolutionPicker from '../ThreediumSolutionPicker';

/**
 * BuilderStartScreen - Initial screen for starting a new build
 * 
 * Options:
 * 1. Use existing Threedium workspace (solution) - creates project with unlimited3d template
 * 2. Create new project from scratch (r3f / gamekit templates)
 *
 * The vanilla `threejs` template is deprecated and hidden here. Existing
 * threejs projects still open and keep the TGP Low/Mid/High presets.
 */
function BuilderStartScreen({ onSelectExperience, onSelectTemplate }) {
  const [selectedSolutionId, setSelectedSolutionId] = useState('');
  const [isCreatingR3F, setIsCreatingR3F] = useState(false);
  const [isCreatingGamekit, setIsCreatingGamekit] = useState(false);
  const [isCreatingFromSolution, setIsCreatingFromSolution] = useState(false);

  // Handle creating a project from a Threedium solution
  const handleUseWorkspace = async () => {
    if (!selectedSolutionId) return;
    
    setIsCreatingFromSolution(true);
    try {
      // Call template handler with unlimited3d template and solution ID
      await onSelectTemplate('unlimited3d', { solutionId: selectedSolutionId });
    } finally {
      setIsCreatingFromSolution(false);
    }
  };

  const handleCreateR3FProject = async () => {
    setIsCreatingR3F(true);
    try {
      await onSelectTemplate('r3f');
    } finally {
      setIsCreatingR3F(false);
    }
  };

  const handleCreateGamekitProject = async () => {
    setIsCreatingGamekit(true);
    try {
      await onSelectTemplate('gamekit');
    } finally {
      setIsCreatingGamekit(false);
    }
  };

  return (
    <div className="builder-start-screen">
      {/* Background Grid Effect */}
      <div className="builder-bg-grid" />

      {/* Main Content Container */}
      <div className="builder-start-container">
        {/* Heading Section */}
        <div className="builder-heading">
          <div className="builder-heading-titles">
            <h1 className="builder-title">
              Build Web Applications
            </h1>
            <h2 className="builder-title-gradient">
              With AI Assistance
            </h2>
          </div>
          <p className="builder-subtitle">
            Use an existing Threedium workspace or start a fresh project
          </p>
        </div>

        {/* Cards Section */}
        <div className="builder-cards">
          {/* Use Existing Workspace (Threedium Solution) Card */}
          <div
            className={`builder-card ${selectedSolutionId ? 'selected' : ''}`}
          >
            <div className="builder-card-content">
              <div className="builder-card-icon">
                <WorkspaceIcon />
              </div>
              <h3 className="builder-card-title">
                Use existing<br />workspace
              </h3>
              <p className="builder-card-description">
                Select a 3D experience from your Threedium account.
              </p>
            </div>

            {/* Threedium Solution Picker */}
            <div className="builder-solution-picker-container">
              <ThreediumSolutionPicker
                value={selectedSolutionId}
                onChange={setSelectedSolutionId}
                placeholder="Search your Threedium solutions..."
                disabled={isCreatingFromSolution}
              />
            </div>

            {/* Use Workspace Button */}
            {selectedSolutionId && (
              <button
                className="builder-use-btn"
                onClick={handleUseWorkspace}
                disabled={isCreatingFromSolution}
              >
                {isCreatingFromSolution ? 'Creating...' : 'Use This Workspace'}
              </button>
            )}
          </div>

          {/* Create R3F Project Card */}
          <div className="builder-card">
            <div className="builder-card-content">
              <div className="builder-card-icon">
                <R3FIcon />
              </div>
              <h3 className="builder-card-title">
                React Three<br />Fiber project
              </h3>
              <p className="builder-card-description">
                Declarative 3D with R3F, Drei, and Zustand. Multi-agent team.
              </p>
            </div>
            <button
              className="builder-template-btn"
              onClick={handleCreateR3FProject}
              disabled={isCreatingR3F}
            >
              {isCreatingR3F ? 'Creating...' : 'Create R3F Project'}
            </button>
          </div>

          <div className="builder-card">
            <div className="builder-card-content">
              <div className="builder-card-icon">
                <GamekitIcon />
              </div>
              <h3 className="builder-card-title">
                Game<br />Studio
              </h3>
              <p className="builder-card-description">
                Cinematic Three.js kit for any genre — shooter, racer, walker, puzzle. Studio, lookdev, and atelier.
              </p>
            </div>
            <button
              className="builder-template-btn"
              onClick={handleCreateGamekitProject}
              disabled={isCreatingGamekit}
            >
              {isCreatingGamekit ? 'Creating...' : 'Create Game Studio Project'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Icon Components
function WorkspaceIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 1L14 4.5V11.5L8 15L2 11.5V4.5L8 1Z" stroke="#60D2CC" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M8 15V8" stroke="#60D2CC" strokeWidth="1.2" />
      <path d="M2 4.5L8 8L14 4.5" stroke="#60D2CC" strokeWidth="1.2" />
    </svg>
  );
}

function R3FIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M8 1L14 4.5V11.5L8 15L2 11.5V4.5L8 1Z" stroke="#60D2CC" strokeWidth="1.2" strokeLinejoin="round" />
      <path d="M8 15V8" stroke="#60D2CC" strokeWidth="1.2" />
      <path d="M2 4.5L8 8L14 4.5" stroke="#60D2CC" strokeWidth="1.2" />
      <circle cx="5" cy="6.5" r="0.8" fill="#60D2CC" />
      <circle cx="11" cy="6.5" r="0.8" fill="#60D2CC" />
      <circle cx="8" cy="11" r="0.8" fill="#60D2CC" />
    </svg>
  );
}

function GamekitIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="2" y="5" width="12" height="8" rx="2" stroke="#60D2CC" strokeWidth="1.2" />
      <circle cx="6" cy="9" r="1.2" stroke="#60D2CC" strokeWidth="1.2" />
      <path d="M10 7.5H12.5M11.25 6.25V8.75" stroke="#60D2CC" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

export default BuilderStartScreen;
